import type { ComponentInit } from "@scripts/registry";

const INPUT = "[data-fa-subscribe-callout-input]";
const MESSAGE = "[data-fa-subscribe-callout-message]";
const ALERT = "[data-fa-subscribe-callout-alert]";
const HONEYPOT = "[data-fa-subscribe-callout-honeypot]";
const SUBMIT = "[data-fa-subscribe-callout-submit]";

const STATE_CLASSES = [
	"fa-subscribe-callout--submitting",
	"fa-subscribe-callout--submitted",
	"fa-subscribe-callout--error",
];

export const initSubscribeCallout: ComponentInit = (element) => {
	const form = element as HTMLFormElement;
	const input = form.querySelector<HTMLInputElement>(INPUT);
	const message = form.parentElement?.querySelector<HTMLElement>(MESSAGE);
	const alert = form.parentElement?.querySelector<HTMLElement>(ALERT);

	if (!input || !message || !alert) return undefined;

	const honeypot = form.querySelector<HTMLInputElement>(HONEYPOT);
	const submit = form.querySelector<HTMLElement>(SUBMIT);
	const success = form.dataset.faSubscribeSuccess ?? "";
	const failure = form.dataset.faSubscribeError ?? "";

	/* Snapshotted before anything is written: teardown has to return the markup to the state the
	   HTML panel already sampled, not merely detach listeners. */
	const initial = {
		message: message.textContent ?? "",
		alert: alert.textContent ?? "",
		invalid: input.getAttribute("aria-invalid"),
		disabled: submit?.getAttribute("aria-disabled") ?? null,
	};

	const controller = new AbortController();
	let inFlight = false;

	const setState = (state: string | null) => {
		form.classList.remove(...STATE_CLASSES);
		if (state !== null) form.classList.add(state);
	};

	const announce = (region: HTMLElement, text: string) => {
		message.textContent = "";
		alert.textContent = "";
		region.textContent = text;
	};

	const send = async () => {
		inFlight = true;
		setState("fa-subscribe-callout--submitting");
		submit?.setAttribute("aria-disabled", "true");
		input.removeAttribute("aria-invalid");
		announce(message, "");

		try {
			const response = await fetch(form.action, {
				method: (form.method || "post").toUpperCase(),
				body: new FormData(form),
				headers: { Accept: "application/json" },
				signal: controller.signal,
			});

			if (!response.ok) throw new Error(String(response.status));

			setState("fa-subscribe-callout--submitted");
			announce(message, success);
			form.reset();
		} catch {
			if (controller.signal.aborted) return;

			setState("fa-subscribe-callout--error");
			announce(alert, failure);
			input.setAttribute("aria-invalid", "true");
		} finally {
			inFlight = false;
			submit?.removeAttribute("aria-disabled");
		}
	};

	form.addEventListener(
		"submit",
		(event) => {
			/* Trapped: report success and send nothing. A bot told it was rejected adapts. */
			if (honeypot !== null && honeypot.value !== "") {
				event.preventDefault();
				setState("fa-subscribe-callout--submitted");
				announce(message, success);
				return;
			}

			/* Not prevented, so the browser blocks it and shows its own message. */
			if (!form.checkValidity()) return;

			event.preventDefault();
			if (inFlight) return;
			void send();
		},
		{ signal: controller.signal },
	);

	return () => {
		controller.abort();
		setState(null);
		form.classList.remove("fa-subscribe-callout--invalid");
		message.textContent = initial.message;
		alert.textContent = initial.alert;

		if (initial.invalid === null) input.removeAttribute("aria-invalid");
		else input.setAttribute("aria-invalid", initial.invalid);

		if (submit !== null && submit !== undefined) {
			if (initial.disabled === null) submit.removeAttribute("aria-disabled");
			else submit.setAttribute("aria-disabled", initial.disabled);
		}
	};
};
